import { create } from 'zustand';

import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from '@/shared/constants/pagination';

import type {
  MedicalStaffSortKey,
  SortDirection,
} from '../types/medical-staff.types';

export interface MedicalStaffListFilters {
  // '' = "All Departments".
  department: string;
  // Free-text — case-insensitive partial match on name / email / department (PRD §3.2).
  search: string;
}

export interface MedicalStaffListSort {
  key: MedicalStaffSortKey;
  dir: SortDirection;
}

// PRD §2.2 #6: rows-per-page dropdown options — shared 20/30/50 set (SHCT-715).
export const MEDICAL_STAFF_PER_PAGE_OPTIONS = PAGE_SIZE_OPTIONS;

interface MedicalStaffListState {
  filters: MedicalStaffListFilters;
  sort: MedicalStaffListSort;
  page: number;
  perPage: number;
  setFilters: (partial: Partial<MedicalStaffListFilters>) => void;
  // Same key → toggle dir; new key → asc.
  toggleSort: (key: MedicalStaffSortKey) => void;
  setPage: (page: number) => void;
  setPerPage: (perPage: number) => void;
  reset: () => void;
}

const INITIAL_FILTERS: MedicalStaffListFilters = { department: '', search: '' };
// PRD §4.1: ID is sortable Desc by default (most recently registered first).
const INITIAL_SORT: MedicalStaffListSort = { key: 'id', dir: 'desc' };

export const useMedicalStaffListStore = create<MedicalStaffListState>((set) => ({
  filters: INITIAL_FILTERS,
  sort: INITIAL_SORT,
  page: 1,
  perPage: DEFAULT_PAGE_SIZE,
  // Filter / sort / perPage changes reset pagination to page 1 (prototype behavior).
  setFilters: (partial) =>
    set((s) => ({ filters: { ...s.filters, ...partial }, page: 1 })),
  toggleSort: (key) =>
    set((s) => ({
      sort:
        s.sort.key === key
          ? { key, dir: s.sort.dir === 'asc' ? 'desc' : 'asc' }
          : { key, dir: 'asc' },
      page: 1,
    })),
  setPage: (page) => set({ page }),
  setPerPage: (perPage) => set({ perPage, page: 1 }),
  reset: () =>
    set({
      filters: INITIAL_FILTERS,
      sort: INITIAL_SORT,
      page: 1,
      perPage: DEFAULT_PAGE_SIZE,
    }),
}));
